/**
 * useUnitSystem Hook
 *
 * The unit system follows the app language (English → US customary, every
 * other language → metric; see utils/unitLocalization.ts). `toggleSystem`
 * flips it for the current screen only, e.g. to show a responder the other
 * system on the emergency profile.
 */

import { useCallback, useState } from "react";
import { useI18nStore } from "../stores/i18nStore";
import { useChosenSystems } from "../stores/unitPreferencesStore";
import {
  formatHeight as fmtHeight,
  formatTemperature,
  formatWeight as fmtWeight,
  getUnitSystem,
  type QuantitySystems,
  type UnitSystem,
  unitLabel,
} from "../utils/unitLocalization";

export type { UnitSystem };

export interface UnitFormatter {
  system: UnitSystem;
  toggleSystem: () => void;
  formatWeight: (kg: number | null | undefined) => string;
  formatHeight: (cm: number | null | undefined) => string;
  formatTemp: (celsius: number | null | undefined) => string;
  weightLabel: string;
  heightLabel: string;
  tempLabel: string;
}

export function useUnitSystem(): UnitFormatter {
  const language = useI18nStore((st) => st.language);
  const chosen = useChosenSystems();
  const [flipped, setFlipped] = useState(false);
  const flip = (x: UnitSystem): UnitSystem =>
    x === "metric" ? "imperial" : "metric";
  // Per quantity: the explicit choice, else the language; the toggle flips each.
  const pick = (q: "weight" | "height" | "temperature"): UnitSystem => {
    const base = chosen?.[q] ?? getUnitSystem(language);
    return flipped ? flip(base) : base;
  };
  const systems: QuantitySystems = {
    weight: pick("weight"),
    height: pick("height"),
    temperature: pick("temperature"),
  };
  const system = systems.weight!;

  const toggleSystem = useCallback(() => setFlipped((f) => !f), []);

  return {
    system,
    toggleSystem,
    formatWeight: (kg) => fmtWeight(kg, language, systems),
    formatHeight: (cm) => fmtHeight(cm, language, systems),
    formatTemp: (c) => formatTemperature(c, language, systems),
    weightLabel: unitLabel("weight", language, systems),
    heightLabel: systems.height === "metric" ? "cm" : "ft/in",
    tempLabel: unitLabel("temperature", language, systems),
  };
}
